import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarClock, Camera, ChevronDown, FileText, Flag, Link2, Paperclip, ShoppingCart, SlidersHorizontal, Tag, Type, Upload, User, UserPlus, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import { cn } from '../../../lib/cn';
import { toLocalDateTime } from '../../../lib/datetime';
import { formatDateTimeRO } from '../../../lib/format';
import { ticketErrorRO } from '../ticketCopy';
import { useAddAttachmentMutation, useAddReferenceMutation, useCreateTicketMutation, useUpdateTicketMutation } from '../tickets.api';
import { TICKET_REFERENCE_LABELS, TicketFormSchema, type Ticket, type TicketForm, type TicketReferenceType } from '../tickets.schema';
import { useLabelColorMap } from '../useLabelColors';

import { AddReferenceDialog } from './AddReferenceDialog';
import { ColleaguePickerDialog } from './ColleaguePickerDialog';
import { LabelChip } from './LabelChip';
import { LabelInput } from './LabelInput';
import { PriorityPicker } from './PriorityPicker';

export type PendingRef = { itemType: TicketReferenceType; itemId: string; label: string };

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    ticket: Ticket | null;
    // Fired after a successful create (not edit) — lets the list clear any filter
    // that would hide the brand-new ticket.
    onCreated?: () => void;
    // Create mode: references pre-filled when the dialog opens (entity pages
    // pass their own object so the ticket is born already linked).
    initialRefs?: PendingRef[];
}

const emptyDefaults: TicketForm = {
    title: '',
    description: '',
    priority: 'normal',
    dueDate: '',
    assigneeUserId: null,
    assigneeName: null,
    labels: [],
};

// Map server field-error codes to a specific Romanian message per field.
const FIELD_ERR: Record<string, string> = {
    required: 'Camp obligatoriu',
    invalid: 'Valoare invalida',
    duplicate: 'Exista deja',
};

const DUE_PRESETS = [
    { label: 'Azi', days: 0 },
    { label: 'Maine', days: 1 },
    { label: 'In 3 zile', days: 3 },
    { label: 'Saptamana viitoare', days: 7 },
];

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

function readAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('read_failed'));
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(file);
    });
}

// A datetime-local value (local tz) for `daysFromNow` at 18:00, for the chips.
// "Azi" after 18:00 falls back to end-of-day so the preset can't produce a
// deadline that's already in the past (an instantly-overdue ticket).
function presetDue(daysFromNow: number): string {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    d.setHours(18, 0, 0, 0);
    if (d.getTime() <= Date.now()) d.setHours(23, 59, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TicketFormDialog({ open, onOpenChange, ticket, onCreated, initialRefs }: Props) {
    const isEdit = ticket !== null;
    const [createTicket, createState] = useCreateTicketMutation();
    const [updateTicket, updateState] = useUpdateTicketMutation();
    const [addAttachment] = useAddAttachmentMutation();
    // `busy` also spans the post-create attachment uploads, which the mutation
    // isLoading flags alone don't cover (FileReader gaps between calls).
    const [busy, setBusy] = useState(false);
    const isSubmitting = busy || createState.isLoading || updateState.isLoading;
    const [assigneeOpen, setAssigneeOpen] = useState(false);
    // Photos/files picked before the ticket exists (create mode only); uploaded
    // right after the create succeeds. Edit mode keeps using AttachmentsPanel.
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const cameraRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    // References picked before the ticket exists (create mode only); attached
    // right after the create succeeds. Edit mode uses ReferencesPanel.
    const [pendingRefs, setPendingRefs] = useState<PendingRef[]>([]);
    const [refDialogOpen, setRefDialogOpen] = useState(false);
    // Create mode starts with title + description + assignee so the common
    // "write it down and hand it off" ticket needs no expanding; everything
    // else (urgency, deadline, files, labels...) hides behind "Mai multe
    // optiuni". Edit mode always shows everything — existing values must
    // never be invisible.
    const [showMore, setShowMore] = useState(false);
    // Read through a ref so an inline `initialRefs={[...]}` prop (new identity
    // every render) can't retrigger the open-reset effect and wipe the form.
    const initialRefsRef = useRef(initialRefs);
    initialRefsRef.current = initialRefs;
    const [addReference] = useAddReferenceMutation();
    const colorMap = useLabelColorMap();

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setValue,
        watch,
        formState: { errors },
    } = useForm<TicketForm>({
        resolver: zodResolver(TicketFormSchema),
        defaultValues: emptyDefaults,
    });

    useEffect(() => {
        if (!open) return;
        if (ticket) {
            reset({
                title: ticket.title,
                description: ticket.description ?? '',
                priority: ticket.priority,
                dueDate: toLocalDateTime(ticket.dueDate),
                assigneeUserId: ticket.assigneeUserId ?? null,
                assigneeName: ticket.assigneeName ?? null,
                labels: ticket.labels?.map((l) => l.label) ?? [],
            });
        } else {
            reset(emptyDefaults);
        }
        setPendingFiles([]);
        setPendingRefs(ticket ? [] : initialRefsRef.current ?? []);
        setShowMore(!!ticket);
    }, [open, ticket, reset]);

    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) {
            if (f.size > MAX_ATTACHMENT_BYTES) {
                toast.error('Fisierul depaseste 20 MB.');
            } else {
                setPendingFiles((prev) => [...prev, f]);
            }
        }
        e.target.value = '';
    };

    const priority = watch('priority');
    const dueDate = watch('dueDate');
    const assigneeUserId = watch('assigneeUserId');
    const assigneeName = watch('assigneeName');
    const labels = watch('labels');

    const onSubmit = handleSubmit(async (values) => {
        setBusy(true);
        try {
            if (isEdit && ticket) {
                await updateTicket({ id: ticket.id, values }).unwrap();
                toast.success('Ticketul a fost actualizat.');
            } else {
                const created = await createTicket(values).unwrap();
                // Ticket exists from here on: attachment/reference failures
                // degrade to a warning (retry from the ticket page), never a
                // failed create.
                let failed = 0;
                for (const f of pendingFiles) {
                    try {
                        await addAttachment({
                            id: created.id,
                            filename: f.name,
                            contentType: f.type || 'application/octet-stream',
                            data: await readAsDataURL(f),
                        }).unwrap();
                    } catch {
                        failed++;
                    }
                }
                for (const r of pendingRefs) {
                    try {
                        await addReference({ id: created.id, ...r }).unwrap();
                    } catch {
                        failed++;
                    }
                }
                if (failed > 0) {
                    toast.error(`Ticket creat, dar ${failed} ${failed === 1 ? 'element nu s-a putut adauga' : 'elemente nu s-au putut adauga'}. Incearca din pagina ticketului.`);
                } else {
                    toast.success(
                        values.assigneeUserId && values.assigneeName
                            ? `Ticket creat si atribuit lui ${values.assigneeName}.`
                            : 'Ticketul a fost creat.',
                    );
                }
                onCreated?.();
            }
            onOpenChange(false);
        } catch (e) {
            const err = e as { message?: string; fields?: Record<string, string> };
            if (err?.fields) {
                for (const [field, code] of Object.entries(err.fields)) {
                    setError(field as keyof TicketForm, {
                        type: 'server',
                        message: FIELD_ERR[code] ?? 'Valoare invalida',
                    });
                }
                return;
            }
            toast.error(ticketErrorRO(err, 'Eroare la salvarea ticketului.'));
        } finally {
            setBusy(false);
        }
    });

    return (
        <Dialog
            open={open}
            closeOnBackdrop={false}
            onOpenChange={onOpenChange}
            maxWidth="max-w-2xl"
            title={isEdit ? 'Editeaza ticketul' : 'Ticket nou'}
            description={isEdit ? 'Spune pe scurt ce trebuie facut, cat e de urgent si cine se ocupa.' : 'Scrie ce trebuie facut si gata — restul e optional.'}
            footer={
                <>
                    <Button type="button" variant="secondary" size="lg" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                        Anuleaza
                    </Button>
                    <Button type="button" size="lg" onClick={onSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Se salveaza...' : isEdit ? 'Salveaza' : 'Creeaza ticket'}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-5" onSubmit={onSubmit}>
                <Field label="Ce trebuie facut?" icon={Type} error={errors.title?.message}>
                    <Input
                        autoFocus={!isEdit}
                        className="h-12 text-base"
                        aria-label="Ce trebuie facut"
                        placeholder="Ex: Aranjeaza raftul de boostere"
                        {...register('title')}
                    />
                </Field>

                <Field label="Detalii" icon={FileText} error={errors.description?.message} hint="Optional.">
                    <Textarea
                        rows={3}
                        className="text-base"
                        aria-label="Detalii"
                        placeholder="Orice e bine de stiut despre ce trebuie facut..."
                        {...register('description')}
                    />
                </Field>

                <Field label="Cine se ocupa?" icon={User} error={errors.assigneeUserId?.message} hint="Optional. Lasa gol pentru neatribuit.">
                    <button
                        type="button"
                        onClick={() => setAssigneeOpen(true)}
                        className="flex h-12 w-full items-center gap-3 rounded-md border border-solid border-gray-300 bg-white px-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                        {assigneeUserId != null ? (
                            <>
                                <Avatar name={assigneeName} seed={assigneeUserId} className="h-8 w-8 text-xs" />
                                <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
                                    {assigneeName || 'Coleg selectat'}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700">
                                    <UserPlus aria-hidden="true" className="h-5 w-5" />
                                </span>
                                <span className="min-w-0 flex-1 text-gray-500 dark:text-gray-400">Alege un coleg</span>
                            </>
                        )}
                        <ChevronDown aria-hidden="true" className="h-5 w-5 shrink-0 text-gray-400" />
                    </button>
                </Field>

                {!showMore && (
                    <button
                        type="button"
                        onClick={() => setShowMore(true)}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-transparent text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40"
                    >
                        <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
                        Mai multe optiuni: urgenta, termen, poze, etichete...
                        <ChevronDown aria-hidden="true" className="h-4 w-4" />
                    </button>
                )}

                {showMore && (
                <>
                <Field label="Cat e de urgent?" icon={Flag} error={errors.priority?.message}>
                    <PriorityPicker value={priority} onChange={(p) => setValue('priority', p, { shouldDirty: true })} />
                </Field>

                <Field
                    label="Pana cand?"
                    icon={CalendarClock}
                    error={errors.dueDate?.message}
                    hint="Optional. Fara termen, ticketul nu apare ca depasit."
                >
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap gap-2">
                            {DUE_PRESETS.map((p) => {
                                const val = presetDue(p.days);
                                const active = dueDate === val;
                                return (
                                    <button
                                        key={p.label}
                                        type="button"
                                        onClick={() => setValue('dueDate', val, { shouldDirty: true })}
                                        className={cn(
                                            'inline-flex h-10 items-center rounded-full border border-solid px-4 text-sm font-semibold transition-colors',
                                            active
                                                ? 'border-blue-600 bg-blue-600 text-white shadow-sm dark:border-blue-500 dark:bg-blue-500'
                                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
                                        )}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                            {dueDate && (
                                <button
                                    type="button"
                                    onClick={() => setValue('dueDate', '', { shouldDirty: true })}
                                    className="inline-flex h-10 items-center rounded-full px-4 text-sm font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
                                >
                                    Sterge termenul
                                </button>
                            )}
                        </div>
                        {/* The chips only highlight an exact match, so an existing
                            deadline (e.g. when editing) would otherwise be invisible
                            until the collapsed exact-date input is expanded. */}
                        {dueDate && (
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Termen ales: {formatDateTimeRO(dueDate)}
                            </p>
                        )}
                        <details className="text-sm text-gray-500 dark:text-gray-400">
                            <summary className="cursor-pointer select-none">sau alege data exacta</summary>
                            <Input type="datetime-local" className="mt-2 h-12 text-base" {...register('dueDate')} />
                        </details>
                    </div>
                </Field>

                {!isEdit && (
                    <Field
                        label="Poze si fisiere"
                        icon={Paperclip}
                        hint="Optional. Se ataseaza ticketului imediat dupa creare."
                    >
                        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickFile} />
                        <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="secondary" disabled={isSubmitting} onClick={() => cameraRef.current?.click()}>
                                <Camera aria-hidden="true" className="h-4 w-4" />
                                Fa o poza
                            </Button>
                            <Button type="button" variant="secondary" disabled={isSubmitting} onClick={() => fileRef.current?.click()}>
                                <Upload aria-hidden="true" className="h-4 w-4" />
                                Incarca fisier
                            </Button>
                        </div>
                        {pendingFiles.length > 0 && (
                            <ul className="mt-1 flex flex-col gap-1.5">
                                {pendingFiles.map((f, i) => (
                                    <li
                                        key={`${f.name}-${i}`}
                                        className="flex items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                    >
                                        <span className="inline-flex min-w-0 items-center gap-2">
                                            <Paperclip aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                                            <span className="truncate">{f.name}</span>
                                        </span>
                                        <button
                                            type="button"
                                            aria-label={`Renunta la ${f.name}`}
                                            className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                            onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                                        >
                                            <X aria-hidden="true" className="h-4 w-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Field>
                )}

                {!isEdit && (
                    <Field
                        label="Legaturi"
                        icon={Link2}
                        hint="Optional. Leaga ticketul de o comanda sau un utilizator."
                    >
                        {pendingRefs.length > 0 && (
                            <ul className="mb-1 flex flex-col gap-1.5">
                                {pendingRefs.map((r, i) => (
                                    <li
                                        key={`${r.itemType}-${r.itemId}`}
                                        className="flex items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                    >
                                        <span className="inline-flex min-w-0 items-center gap-2">
                                            {r.itemType === 'user' ? (
                                                <User aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                                            ) : (
                                                <ShoppingCart aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                                            )}
                                            <span className="truncate">
                                                {TICKET_REFERENCE_LABELS[r.itemType]}: {r.label}
                                            </span>
                                        </span>
                                        <button
                                            type="button"
                                            aria-label={`Renunta la legatura ${r.label}`}
                                            className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                            onClick={() => setPendingRefs((prev) => prev.filter((_, j) => j !== i))}
                                        >
                                            <X aria-hidden="true" className="h-4 w-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div>
                            <Button type="button" variant="secondary" disabled={isSubmitting} onClick={() => setRefDialogOpen(true)}>
                                <Link2 aria-hidden="true" className="h-4 w-4" />
                                Adauga legatura
                            </Button>
                        </div>
                    </Field>
                )}

                <Field label="Etichete" icon={Tag} hint="Optional. Scrie si apasa Enter; Tab completeaza din cele existente.">
                    {labels.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                            {labels.map((l) => (
                                <LabelChip
                                    key={l}
                                    label={l}
                                    colorKey={colorMap[l.toLowerCase()]}
                                    onRemove={() =>
                                        setValue('labels', labels.filter((x) => x !== l), { shouldDirty: true })
                                    }
                                />
                            ))}
                        </div>
                    )}
                    <LabelInput
                        existing={labels}
                        onAdd={(l) => {
                            if (!labels.some((x) => x.toLowerCase() === l.toLowerCase())) {
                                setValue('labels', [...labels, l], { shouldDirty: true });
                            }
                        }}
                    />
                </Field>
                </>
                )}
            </form>

            <ColleaguePickerDialog
                open={assigneeOpen}
                onOpenChange={setAssigneeOpen}
                selectedId={assigneeUserId}
                onSelect={(v) => {
                    setValue('assigneeUserId', v ? v.assigneeUserId : null, { shouldDirty: true });
                    setValue('assigneeName', v ? v.assigneeName : null, { shouldDirty: true });
                }}
            />

            {!isEdit && (
                <AddReferenceDialog
                    open={refDialogOpen}
                    onOpenChange={setRefDialogOpen}
                    onPick={(ref) =>
                        setPendingRefs((prev) =>
                            prev.some((p) => p.itemType === ref.itemType && p.itemId === ref.itemId)
                                ? prev
                                : [...prev, ref],
                        )
                    }
                />
            )}
        </Dialog>
    );
}

function Field({
    label,
    icon: Icon,
    error,
    hint,
    children,
}: {
    label: string;
    icon?: typeof Type;
    error?: string;
    hint?: string;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                {Icon && <Icon aria-hidden="true" className="h-4 w-4 text-gray-400" />}
                {label}
            </span>
            {children}
            {error ? (
                <span className="text-xs font-medium text-red-600 dark:text-red-400">{error}</span>
            ) : hint ? (
                <span className="text-xs text-gray-500 dark:text-gray-400">{hint}</span>
            ) : null}
        </div>
    );
}
